import { TFunction } from 'i18next';

import { Campaign, DiscountType, LoyaltyType, LoyaltyDiscountType } from '../types';

interface FormikErrors {
    name?: string;
    dateFrom?: string;
    dateTo?: string;
    discounts?: DiscountError[];
}

interface DiscountError {
    discountValue: string;
    totalPurchase: string;
    payFor: string;
    title: string;
    description: string;
    name: string;
    visitsNumber: string;
    requirements?: RequirementsError[];
    rewardValue: string;
    minimumPurchase: string;
    discountUsageAmount: string;
    dateFrom: string;
    dateTo: string;
}

interface RequirementsError {
    timeFrom: Date;
    timeTo: Date;
}

export const validate = (values: Campaign, t: TFunction) => {
    const errors: FormikErrors = { discounts: []};
    if (!values.name) {
        errors.name = t('marketing.campaign.required');
    }
    const fromDate = new Date(values.dateFrom);
    fromDate.setHours(values.timeFrom.getHours(), values.timeFrom.getMinutes());
    const toDate = new Date(values.dateTo);
    toDate.setHours(values.timeTo.getHours(), values.timeTo.getMinutes());
    if (fromDate.getTime() > toDate.getTime()) {
        errors.dateFrom = t('marketing.campaign.date_from_bigger');
    }
    if (fromDate.getTime() === toDate.getTime()) {
        errors.dateTo = t('marketing.campaign.same_active_to_from');
    }
    // eslint-disable-next-line array-callback-return
    values.discounts.map((discount, index) => {
        if (errors.discounts) {
            const discountErrors = errors.discounts[index]
                ? errors.discounts[index]
                : {
                    requirements: [] as RequirementsError[],
                } as DiscountError;

            if (!discount.discountValue && discount.loyaltyDiscountType !== LoyaltyDiscountType.freeItem) {
                discountErrors.discountValue = t('marketing.campaign.required');
            }
            if (discount.discountValue && Number(discount.discountValue) < 0) {
                discountErrors.discountValue = t('marketing.campaign.smaller_than_count', { count: 0 });
            }
            if (
                (discount.discountUsageAmount !== undefined || discount.discountUsageAmount !== '') &&
                (Number(discount.discountUsageAmount) < -1 || Number(discount.discountUsageAmount) === 0)) {
                // allow -1 for unlimited or a positive number but not 0
                discountErrors.discountUsageAmount = t('marketing.campaign.smaller_than_count', { count: 1 });
            }
            if (
                discount.discountValue &&
                discount.type === DiscountType.discountInPercentage &&
                Number(discount.discountValue) > 100
            ) {
                discountErrors.discountValue = t('marketing.campaign.bigger_than_hundred');
            }
            // if (!discount.totalPurchase && discount.type === DiscountType.discountInNok) {
            //     discountErrors.totalPurchase = t('marketing.campaign.required');
            // }
            // if (
            //     discount.totalPurchase &&
            //     Number(discount.totalPurchase) < 0 &&
            //     discount.type === DiscountType.discountInNok
            // ) {
            //     discountErrors.totalPurchase = t('marketing.campaign.smaller_than_count', {count: 0});
            // }
            if (!discount.payFor && discount.type === DiscountType.amountBuyDiscount) {
                discountErrors.payFor = t('marketing.campaign.required');
            }
            if (discount.payFor && Number(discount.payFor) < 0 && discount.type === DiscountType.amountBuyDiscount) {
                discountErrors.payFor = t('marketing.campaign.smaller_than_count', { count: 0 });
            }
            if (!discount.title) {
                discountErrors.title = t('marketing.campaign.required');
            }
            if (!discount.description) {
                discountErrors.description = t('marketing.campaign.required');
            }
            if (discount.type === DiscountType.loyalty) {
                if (!discount.visitsNumber && discount.loyaltyType === LoyaltyType.visitAndSpending) {
                    discountErrors.visitsNumber = t('marketing.campaign.required');
                }
                if (
                    discount.visitsNumber &&
                    discount.loyaltyType === LoyaltyType.visitAndSpending &&
                    Number(discount.discountValue) < 0
                ) {
                    discountErrors.visitsNumber = t('marketing.campaign.smaller_than_count', { count: 0 });
                }
                if (
                    discount.discountValue &&
                    discount.loyaltyDiscountType === LoyaltyDiscountType.inPercentage &&
                    Number(discount.discountValue) > 100
                ) {
                    discountErrors.rewardValue = t('marketing.campaign.bigger_than_hundred');
                }
                if (!discount.minimumPurchase && discount.loyaltyType === LoyaltyType.visitAndSpending) {
                    discountErrors.minimumPurchase = t('marketing.campaign.required');
                }
                if (
                    discount.minimumPurchase &&
                    discount.loyaltyType === LoyaltyType.visitAndSpending &&
                    Number(discount.minimumPurchase) < 0
                ) {
                    discountErrors.minimumPurchase = t('marketing.campaign.smaller_than_count', { count: 0 });
                }

                // eslint-disable-next-line array-callback-return
                discount.requirements?.map((requirement, index) => {
                    if (discountErrors.requirements) {
                        const requirementsErrors = discountErrors.requirements[index]
                            ? discountErrors.requirements[index]
                            : ({} as RequirementsError);
                        if (
                            requirement.timeFrom.getTime() > requirement.timeTo.getTime() &&
                            discount.loyaltyType === LoyaltyType.visitAndSpending
                        ) {
                            requirementsErrors.timeFrom = t('marketing.campaign.visible_from_bigger');
                        }
                        if (
                            requirement.timeFrom.getTime() === requirement.timeTo.getTime() &&
                            discount.loyaltyType === LoyaltyType.visitAndSpending
                        ) {
                            requirementsErrors.timeFrom = t('marketing.campaign.visible_from_equal');
                        }
                        discountErrors.requirements[index] = requirementsErrors;
                    }
                });
            }
            if (
                discountErrors.requirements &&
                discountErrors.requirements.every((requirement) => Object.keys(requirement).length === 0)
            ) {
                delete discountErrors.requirements;
            }
            errors.discounts[index] = discountErrors;
        }
    });
    if (errors.discounts && errors.discounts.every((discount) => Object.keys(discount).length === 0)) {
        delete errors.discounts;
    }
    return errors;
};
